'use client';

import { createContext, ReactNode, useContext } from "react";
import { SiteResearch, SiteResearchInput } from "@/app/lib/generated/graphql-types";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { SiteResearchSchema } from "@/app/features/sites/research/types/schema";
import type { SiteResearchEditable } from "@/app/features/sites/research/types/types";
import { getSiteResearch } from "@/app/features/sites/research/data-access/queries";
import { updateSiteResearch } from "@/app/features/sites/research/data-access/mutations";
import {
    editableHeroIntroFrom,
    heroIntroInputFrom,
} from "@/app/features/shared/intros/hero/types/HeroIntro.schema";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";

const SiteResearchEditorContext = createContext<UseSingleTypeEditorType<SiteResearchEditable> | undefined>(undefined);

function editableFromSiteResearch(site: SiteResearch): SiteResearchEditable {
    return { intro: editableHeroIntroFrom(site.intro) };
}

async function siteResearchInputFrom(editable: SiteResearchEditable): Promise<SiteResearchInput> {
    return { intro: await heroIntroInputFrom(editable.intro) };
}

export function useSiteResearchEditor() {
    const context = useContext(SiteResearchEditorContext);
    if (!context) {
        throw new Error("useSiteResearchEditor must be used within SiteResearchEditorProvider");
    }
    return context;
}

export default function SiteResearchEditorProvider({
    children,
    isDraftModeEnabled,
}: {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}) {
    const editor = useSingleTypeEditor<SiteResearch, SiteResearchEditable, SiteResearchInput>({
        schema: SiteResearchSchema,
        loadEntity: getSiteResearch,
        update: updateSiteResearch,
        toEditable: editableFromSiteResearch,
        toInput: siteResearchInputFrom,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SiteResearchEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={() => void editor.discardChanges()}
            />
        </SiteResearchEditorContext.Provider>
    );
}
